import { c as classNames } from './index-8887fc24.js';
import * as React from 'react';
import { _ as __rest, a as __assign } from './tslib.es6-18cb2814.js';
import { C as ChevronRightIcon } from './ChevronRightIcon-b72559d2.js';
import './countries-34fea817.js';
import { B as Button } from './index-b7114926.js';

var ChevronLeftIcon = function (_a) {
    var _b = _a.fillColor, fillColor = _b === void 0 ? "currentColor" : _b, props = __rest(_a, ["fillColor"]);
    return (React.createElement("svg", __assign({ width: "1em", height: "1em", viewBox: "0 0 25 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, props),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M18.8314 1.42024C19.3918 1.98056 19.3918 2.88901 18.8314 3.44933L10.2808 12L18.8314 20.5507C19.3918 21.111 19.3918 22.0194 18.8314 22.5798C18.2711 23.1401 17.3627 23.1401 16.8024 22.5798L7.23713 13.0145C6.67682 12.4542 6.67682 11.5458 7.23713 10.9855L16.8024 1.42024C17.3627 0.859921 18.2711 0.859921 18.8314 1.42024Z", fill: fillColor })));
};

function _typeof(o) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) {
    return typeof o;
  } : function (o) {
    return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o;
  }, _typeof(o);
}

function toInteger(dirtyNumber) {
  if (dirtyNumber === null || dirtyNumber === true || dirtyNumber === false) {
    return NaN;
  }
  var number = Number(dirtyNumber);
  if (isNaN(number)) {
    return number;
  }
  return number < 0 ? Math.ceil(number) : Math.floor(number);
}

function requiredArgs(required, args) {
  if (args.length < required) {
    throw new TypeError(required + ' argument' + (required > 1 ? 's' : '') + ' required, but only ' + args.length + ' present');
  }
}

/**
 * @name toDate
 * @category Common Helpers
 * @summary Convert the given argument to an instance of Date.
 *
 * @description
 * Convert the given argument to an instance of Date.
 *
 * If the argument is an instance of Date, the function returns its clone.
 *
 * If the argument is a number, it is treated as a timestamp.
 *
 * If the argument is none of the above, the function returns Invalid Date.
 *
 * **Note**: *all* Date arguments passed to any *date-fns* function is processed by `toDate`.
 *
 * @param {Date|Number} argument - the value to convert
 * @returns {Date} the parsed date in the local time zone
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // Clone the date:
 * const result = toDate(new Date(2014, 1, 11, 11, 30, 30))
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert the timestamp to date:
 * const result = toDate(1392098430000)
 * //=> Tue Feb 11 2014 11:30:30
 */
function toDate(argument) {
  requiredArgs(1, arguments);
  var argStr = Object.prototype.toString.call(argument);

  // Clone the date
  if (argument instanceof Date || _typeof(argument) === 'object' && argStr === '[object Date]') {
    // Prevent the date to lose the milliseconds when passed to new Date() in IE10
    return new Date(argument.getTime());
  } else if (typeof argument === 'number' || argStr === '[object Number]') {
    return new Date(argument);
  } else {
    if ((typeof argument === 'string' || argStr === '[object String]') && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn("Starting with v2.0.0-beta.1 date-fns doesn't accept strings as date arguments. Please use `parseISO` to parse strings. See: https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#string-arguments");
      // eslint-disable-next-line no-console
      console.warn(new Error().stack);
    }
    return new Date(NaN);
  }
}

/**
 * @name addMonths
 * @category Month Helpers
 * @summary Add the specified number of months to the given date.
 *
 * @description
 * Add the specified number of months to the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of months to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the months added
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Add 5 months to 1 September 2014:
 * const result = addMonths(new Date(2014, 8, 1), 5)
 * //=> Sun Feb 01 2015 00:00:00
 */
function addMonths(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var date = toDate(dirtyDate);
  var amount = toInteger(dirtyAmount);
  if (isNaN(amount)) {
    return new Date(NaN);
  }
  if (!amount) {
    // If 0 months, no-op to avoid changing times in the hour before end of DST
    return date;
  }
  var dayOfMonth = date.getDate();

  // The JS Date object supports date math by accepting out-of-bounds values for
  // month, day, etc. For example, new Date(2020, 0, 0) returns 31 Dec 2019 and
  // new Date(2020, 13, 1) returns 1 Feb 2021.  This is *almost* the behavior we
  // want except that dates will wrap around the end of a month, meaning that
  // new Date(2020, 13, 31) will return 3 Mar 2021 not 28 Feb 2021 as desired. So
  // we'll default to the end of the desired month by adding 1 to the desired
  // month and using a date of 0 to back up one day to the end of the desired
  // month.
  var endOfDesiredMonth = new Date(date.getTime());
  endOfDesiredMonth.setMonth(date.getMonth() + amount + 1, 0);
  var daysInMonth = endOfDesiredMonth.getDate();
  if (dayOfMonth >= daysInMonth) {
    // If we're already at the end of the month, then this is the correct date
    // and we're done.
    return endOfDesiredMonth;
  } else {
    // Otherwise, we now know that setting the original day-of-month value won't
    // cause an overflow, so set the desired day-of-month. Note that we can't
    // just set the date of `endOfDesiredMonth` because that object may have had
    // its time changed in the unusual case where where a DST transition was on
    // the last day of the month and its local time was in the hour skipped or
    // repeated next to a DST transition.  So we use `date` instead which is
    // guaranteed to still have the original time.
    date.setFullYear(endOfDesiredMonth.getFullYear(), endOfDesiredMonth.getMonth(), dayOfMonth);
    return date;
  }
}

/**
 * @name addYears
 * @category Year Helpers
 * @summary Add the specified number of years to the given date.
 *
 * @description
 * Add the specified number of years to the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of years to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the years added
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Add 5 years to 1 September 2014:
 * const result = addYears(new Date(2014, 8, 1), 5)
 * //=> Sun Sep 01 2019 00:00:00
 */
function addYears(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var amount = toInteger(dirtyAmount);
  return addMonths(dirtyDate, amount * 12);
}

/**
 * @name endOfMonth
 * @category Month Helpers
 * @summary Return the end of a month for the given date.
 *
 * @description
 * Return the end of a month for the given date.
 * The result will be in the local timezone.
 *
 * @param {Date|Number} date - the original date
 * @returns {Date} the end of a month
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // The end of a month for 2 September 2014 11:55:00:
 * const result = endOfMonth(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Tue Sep 30 2014 23:59:59.999
 */
function endOfMonth(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var month = date.getMonth();
  date.setFullYear(date.getFullYear(), month + 1, 0);
  date.setHours(23, 59, 59, 999);
  return date;
}

/**
 * @name startOfMonth
 * @category Month Helpers
 * @summary Return the start of a month for the given date.
 *
 * @description
 * Return the start of a month for the given date.
 * The result will be in the local timezone.
 *
 * @param {Date|Number} date - the original date
 * @returns {Date} the start of a month
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // The start of a month for 2 September 2014 11:55:00:
 * const result = startOfMonth(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Mon Sep 01 2014 00:00:00
 */
function startOfMonth(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  date.setDate(1);
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * @name startOfYear
 * @category Year Helpers
 * @summary Return the start of a year for the given date.
 *
 * @description
 * Return the start of a year for the given date.
 * The result will be in the local timezone.
 *
 * @param {Date|Number} date - the original date
 * @returns {Date} the start of a year
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // The start of a year for 2 September 2014 11:55:00:
 * const result = startOfYear(new Date(2014, 8, 2, 11, 55, 00))
 * //=> Wed Jan 01 2014 00:00:00
 */
function startOfYear(dirtyDate) {
  requiredArgs(1, arguments);
  var cleanDate = toDate(dirtyDate);
  var date = new Date(0);
  date.setFullYear(cleanDate.getFullYear(), 0, 1);
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * @name setYear
 * @category Year Helpers
 * @summary Set the year to the given date.
 *
 * @description
 * Set the year to the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} year - the year of the new date
 * @returns {Date} the new date with the year set
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Set year 2013 to 1 September 2014:
 * const result = setYear(new Date(2014, 8, 1), 2013)
 * //=> Sun Sep 01 2013 00:00:00
 */
function setYear(dirtyDate, dirtyYear) {
  requiredArgs(2, arguments);
  var date = toDate(dirtyDate);
  var year = toInteger(dirtyYear);

  // Check if date is Invalid Date because Date.prototype.setFullYear ignores the value of Invalid Date
  if (isNaN(date.getTime())) {
    return new Date(NaN);
  }
  date.setFullYear(year);
  return date;
}

/**
 * @name subYears
 * @category Year Helpers
 * @summary Subtract the specified number of years from the given date.
 *
 * @description
 * Subtract the specified number of years from the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of years to be subtracted. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the years subtracted
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Subtract 5 years from 1 September 2014:
 * const result = subYears(new Date(2014, 8, 1), 5)
 * //=> Tue Sep 01 2009 00:00:00
 */
function subYears(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var amount = toInteger(dirtyAmount);
  return addYears(dirtyDate, -amount);
}

var styles = {"wrapper":"YearPicker_wrapper_7d255126","picker":"YearPicker_picker_7d255126","years":"YearPicker_years_7d255126","header":"YearPicker_header_7d255126","pickerControl":"YearPicker_pickerControl_7d255126","controls":"YearPicker_controls_7d255126","control":"YearPicker_control_7d255126","cell":"YearPicker_cell_7d255126","focus-visible":"YearPicker_focus-visible_7d255126"};

var YearPicker = React.forwardRef(function YearPicker(_a, ref) {
    var className = _a.className, boundingRange = _a.boundingRange, _b = _a.selectedDate, selectedDate = _b === void 0 ? new Date() : _b, onYearSelect = _a.onYearSelect, _c = _a.numYears, numYears = _c === void 0 ? 10 : _c;
    var _d = React.useState(selectedDate), currentVisibleDate = _d[0], setCurrentVisibleDate = _d[1];
    var _e = boundingRange || [], minDate = _e[0], maxDate = _e[1];
    var visibleYears = React.useMemo(function () {
        var currentYear = currentVisibleDate.getFullYear();
        return Array.from(Array(numYears).keys()).map(function (n) { return n - numYears + currentYear + 1; });
    }, [currentVisibleDate, numYears]);
    var isInRange = React.useCallback(function (date) {
        return (minDate ? startOfYear(date) >= startOfYear(minDate) : true) &&
            (maxDate ? startOfYear(date) <= startOfYear(maxDate) : true);
    }, [maxDate, minDate]);
    var showPrevious = React.useMemo(function () { return isInRange(endOfMonth(subYears(currentVisibleDate, numYears))); }, [currentVisibleDate, isInRange, numYears]);
    var showNext = React.useMemo(function () { return isInRange(startOfMonth(addYears(currentVisibleDate, 1))); }, [currentVisibleDate, isInRange]);
    var onPreviousClick = React.useCallback(function () {
        var newYear = subYears(currentVisibleDate, numYears);
        setCurrentVisibleDate(newYear);
    }, [currentVisibleDate, numYears]);
    var onNextClick = React.useCallback(function () {
        var newYear = addYears(currentVisibleDate, numYears);
        setCurrentVisibleDate(newYear);
    }, [currentVisibleDate, numYears]);
    return (React.createElement("div", { ref: ref, className: classNames(styles.wrapper, className), "data-testid": "year-picker" },
        React.createElement("div", { className: styles.picker },
            React.createElement("div", { className: styles.header },
                React.createElement(Button, { variant: "secondary", color: "interface", size: "small", label: "Previous Years", "data-testid": "year-picker-previous-button", onClick: onPreviousClick, disabled: !showPrevious },
                    React.createElement(ChevronLeftIcon, null)),
                React.createElement("div", { className: styles.pickerControl, "data-testid": "year-picker-header" },
                    currentVisibleDate.getFullYear() - numYears + 1,
                    " - ",
                    currentVisibleDate.getFullYear()),
                React.createElement(Button, { variant: "secondary", color: "interface", size: "small", label: "Next Years", "data-testid": "year-picker-next-button", onClick: onNextClick, disabled: !showNext },
                    React.createElement(ChevronRightIcon, null))),
            React.createElement("div", { className: styles.years }, visibleYears.map(function (year) { return (React.createElement("button", { "data-testid": "year-picker-year", "data-active-year": year === selectedDate.getFullYear(), "data-inrange": isInRange(setYear(selectedDate, year)), className: styles.cell, key: year, onClick: function () {
                    onYearSelect && onYearSelect(setYear(selectedDate, year));
                } }, year)); })))));
});

export { ChevronLeftIcon as C, YearPicker as Y, _typeof as _, toInteger as a, addMonths as b, subYears as c, startOfYear as d, endOfMonth as e, addYears as f, requiredArgs as r, startOfMonth as s, toDate as t };
